import { useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  type ScreenshotHighlight,
  isValidBox,
  resolveBoxViewport,
  resolveHighlightStyles,
} from "./screenshot-types";

/** Pure SVG Renderer embedding the raster screenshot and vector highlight overlays */
export function ScreenshotSvg({
  url,
  alt,
  highlights = [],
  activeHighlightId,
  showHighlights = true,
  onHoverHighlight,
  onSelectHighlight,
  className,
  naturalSize,
}: {
  url: string;
  alt: string;
  highlights?: ScreenshotHighlight[];
  activeHighlightId?: string | null;
  showHighlights?: boolean;
  onHoverHighlight?: (id: string | null) => void;
  onSelectHighlight?: (id: string) => void;
  className?: string;
  naturalSize?: { width: number; height: number } | null;
}) {
  const firstBox = highlights.find((h) => isValidBox(h.box))?.box;
  const { vw, vh } = resolveBoxViewport(firstBox, naturalSize ?? null);

  const validHighlights = useMemo(
    () => highlights.filter((h) => isValidBox(h.box)),
    [highlights],
  );

  return (
    <svg
      viewBox={`0 0 ${vw} ${vh}`}
      preserveAspectRatio="xMidYMid meet"
      className={cn("block max-h-full max-w-full select-none", className)}
      role="img"
      aria-label={alt}
    >
      {/* Raster image rendered directly inside SVG coordinate space */}
      <image href={url} width={vw} height={vh} preserveAspectRatio="none" />

      {/* Vector highlight layers */}
      {showHighlights &&
        validHighlights.map((h, i) => {
          const isActive =
            h.id === activeHighlightId ||
            h.isActive ||
            validHighlights.length === 1;
          const styles = resolveHighlightStyles(h.tone);
          const box = h.box;
          const indexLabel =
            h.index ?? (validHighlights.length > 1 ? i + 1 : null);
          const badgeText = indexLabel
            ? h.label
              ? `#${indexLabel}: ${h.label}`
              : `#${indexLabel}`
            : (h.label ?? null);

          // Collision-aware label placement: flip below if near top boundary
          const flipBelow = box.y < 34;
          const labelY = flipBelow
            ? box.y + box.height + 20
            : Math.max(16, box.y - 8);
          const labelX = Math.max(8, Math.min(box.x + 4, vw - 220));

          return (
            <g
              key={h.id}
              role={onSelectHighlight ? "button" : undefined}
              tabIndex={onSelectHighlight ? 0 : undefined}
              aria-label={
                badgeText || (onSelectHighlight ? "Select finding" : undefined)
              }
              className={cn(
                "group cursor-pointer transition-opacity duration-150 focus-visible:outline-hidden",
                !isActive && validHighlights.length > 1 && activeHighlightId
                  ? "opacity-40"
                  : "opacity-100",
              )}
              onMouseEnter={() => onHoverHighlight?.(h.id)}
              onMouseLeave={() => onHoverHighlight?.(null)}
              onClick={(e) => {
                e.stopPropagation();
                onSelectHighlight?.(h.id);
              }}
              onKeyDown={(e) => {
                if (onSelectHighlight && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelectHighlight(h.id);
                }
              }}
            >
              {/* High-contrast outer stroke to guarantee legibility on any background */}
              <rect
                x={box.x}
                y={box.y}
                width={box.width}
                height={box.height}
                rx={4}
                fill="none"
                stroke="rgba(0,0,0,0.65)"
                strokeWidth={isActive ? 4 : 3}
                style={{ vectorEffect: "non-scaling-stroke" }}
                className="group-focus-visible:stroke-white group-focus-visible:stroke-[5px]"
              />

              {/* Main colored severity stroke and transparent tint fill */}
              <rect
                x={box.x}
                y={box.y}
                width={box.width}
                height={box.height}
                rx={4}
                fill={styles.fill}
                stroke={styles.stroke}
                strokeWidth={isActive ? 2.5 : 1.75}
                style={{ vectorEffect: "non-scaling-stroke" }}
                className={cn(
                  isActive &&
                    "motion-safe:animate-[pulse_1.2s_ease-out_2] filter drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]",
                )}
              />

              {/* Anchored Pill Badge */}
              {badgeText && (
                <g
                  className="pointer-events-none transition-opacity duration-150 group-hover:opacity-20"
                  transform={`translate(${labelX}, ${labelY})`}
                >
                  <rect
                    x={-2}
                    y={-14}
                    width={Math.min(
                      260,
                      Math.max(32, badgeText.length * 7.5 + 16),
                    )}
                    height={18}
                    rx={3}
                    fill={styles.badgeBg}
                    fillOpacity={0.92}
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth={1}
                    style={{ vectorEffect: "non-scaling-stroke" }}
                  />
                  <text
                    x={6}
                    y={-1}
                    fill={styles.badgeText}
                    fontSize={11}
                    fontFamily="Inter, system-ui, sans-serif"
                    fontWeight={600}
                    className="select-none"
                  >
                    {badgeText.length > 32
                      ? `${badgeText.slice(0, 30)}…`
                      : badgeText}
                  </text>
                </g>
              )}
            </g>
          );
        })}
    </svg>
  );
}
